import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  Audience,
  EmailDraft,
  EmailMessage,
  MailAccountView,
  DraftOverviewRow,
  MailCounts,
} from '@epasu/core';
import {
  CheckCheck,
  EyeOff,
  FolderKanban,
  Paperclip,
  FilePen,
  Link2,
  PenSquare,
  RefreshCw,
  Reply,
  Search,
  UserPlus,
} from 'lucide-react';
import { counterpartAddress } from '@epasu/core/counterpart';
import { crm, type MailRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/PageHeader';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AssignMessageDialog } from '@/components/AssignMessageDialog';
import { AssignProjectDialog } from '@/components/AssignProjectDialog';
import { ReplyDialog } from '@/components/ReplyDialog';
import { ReplySentimentButtons, SENTIMENT_LABEL } from '@/components/ReplySentimentButtons';
import { ErstanspracheHinweis } from '@/components/ErstanspracheHinweis';
import { ComposeDialog } from '@/components/ComposeDialog';
import { MessageBody } from '@/components/MessageBody';
import { DraftCard } from '@/components/DraftCard';
import { cn } from '@/lib/utils';

type MailReiter = 'unread' | 'inbound' | 'outbound' | 'unassigned' | 'ignored';
/** „Entwürfe“ ist kein Filter auf Post, sondern eine eigene Liste. */
type InboxReiter = MailReiter | 'drafts';

/**
 * Jeder Reiter beschreibt eine Abfrage — Filter und Zähler gehören fest
 * zusammen. Vor allem bei „Ungelesen“ ist das wichtig: `ingestInbound` legt
 * automatisch ignorierte Eingangspost mit `seen: false` an, anders als das
 * Ignorieren von Hand. Ein Reiter über `filter: 'all'` zählte solche
 * Nachrichten nicht mit dem `unseen`-Zähler mit, zeigte sie aber in der
 * Liste — Zahl und Liste liefen auseinander. Deshalb steht „Ungelesen“ auf
 * `assigned` und benutzt genau den Zähler seiner eigenen Liste.
 */
const REITER: {
  key: MailReiter;
  label: string;
  args: Parameters<typeof crm.mailbox.list>[0];
  zahl: (c: MailCounts) => number;
}[] = [
  {
    key: 'unread',
    label: 'Ungelesen',
    args: { filter: 'assigned', direction: 'inbound', onlyUnseen: true },
    zahl: (c) => c.unseen,
  },
  {
    key: 'inbound',
    label: 'Eingang',
    args: { filter: 'assigned', direction: 'inbound' },
    zahl: (c) => c.inbound,
  },
  {
    key: 'outbound',
    label: 'Ausgang',
    args: { filter: 'all', direction: 'outbound' },
    zahl: (c) => c.outbound,
  },
  {
    key: 'unassigned',
    label: 'Nicht zugeordnet',
    args: { filter: 'unassigned' },
    zahl: (c) => c.unassigned,
  },
  { key: 'ignored', label: 'Ignoriert', args: { filter: 'ignored' }, zahl: (c) => c.ignored },
];

/** Was in jedem Reiter steht, solange nicht gesucht wird. */
const LEERE_LISTE: Record<InboxReiter, string> = {
  unread:
    'Keine ungelesene Post. Neue Nachrichten von zugeordneten Kontakten stehen hier, bis sie geöffnet werden.',
  inbound:
    'Keine eingegangene Kundenpost. Nachrichten erscheinen hier, sobald ihr Absender einem Kontakt zugeordnet ist.',
  outbound: 'Noch nichts verschickt. Antworten und neue E-Mails stehen hier, sobald sie rausgehen.',
  unassigned:
    'Nichts Unzugeordnetes. Post, deren Absender keinem Kontakt zuzuordnen ist, landet hier.',
  drafts:
    'Keine Entwürfe. Wer eine angefangene Antwort schließt, kann sie hier als Entwurf ablegen.',
  ignored:
    'Nichts ignoriert. Werbung und Automaten, die von Hand oder per Regel ausgeblendet wurden, stehen hier.',
};

export function InboxPage() {
  const navigate = useNavigate();
  const [reiter, setReiter] = useState<InboxReiter>('unread');
  const [rows, setRows] = useState<MailRow[]>([]);
  const [counts, setCounts] = useState<MailCounts | null>(null);
  const [thread, setThread] = useState<EmailMessage[] | null>(null);
  const [drafts, setDrafts] = useState<EmailDraft[]>([]);
  const [offenerEntwurf, setOffenerEntwurf] = useState<{
    draft: EmailDraft;
    bezug: EmailMessage;
  } | null>(null);
  const [entwuerfe, setEntwuerfe] = useState<DraftOverviewRow[]>([]);
  const [selected, setSelected] = useState<MailRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [suche, setSuche] = useState('');

  const [assigning, setAssigning] = useState<{
    row: MailRow;
    modus: 'vorhanden' | 'neu';
  } | null>(null);
  /**
   * Ob die Adresse der Gegenseite schon jemandem gehoert. Eine Antwort kann
   * ueber den Verlauf an einem Kontakt haengen und trotzdem von jemand
   * anderem stammen — die Schulleiterin antwortet auf die Mail ans
   * Sekretariat. Dann soll „Kontakt anlegen“ da sein.
   */
  const [adresseBekannt, setAdresseBekannt] = useState<boolean | null>(null);
  const [assigningProject, setAssigningProject] = useState<MailRow | null>(null);
  const [replying, setReplying] = useState<EmailMessage | null>(null);
  const [ignoring, setIgnoring] = useState<MailRow | null>(null);

  const [composing, setComposing] = useState(false);
  // Null heisst „noch nicht geladen“ — der Hinweis auf das fehlende Postfach
  // darf nicht schon waehrend des Ladens aufblitzen.
  const [konten, setKonten] = useState<MailAccountView[] | null>(null);
  // Leer heisst „alle Postfaecher“. Anders als die Suche auf anderen Seiten
  // wird der Filter von keinem Query-Parameter gefuellt, deshalb reicht hier
  // ein einfacher Zustand statt `useQuerySeededState`.
  const [postfach, setPostfach] = useState('');
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [threadsMitEntwurf, setThreadsMitEntwurf] = useState<Set<string>>(new Set());

  // Inaktive Konten rufen nichts mehr ab, taugen also weder als Filter noch
  // als Kennzeichen. Bei hoechstens einem aktiven Postfach bliebe beides
  // wirkungslos: der Filter filterte nichts, das Kuerzel staende an jeder
  // Zeile dasselbe. Dann zeigen wir es gar nicht erst.
  const aktiveKonten = (konten ?? []).filter((k) => k.active);
  const mehrereKonten = aktiveKonten.length > 1;

  const load = useCallback(async () => {
    // Das Suchfeld wirkt innerhalb des aktiven Reiters — es ersetzt dessen
    // Bedingung nicht, sondern kommt zu ihr dazu. Wer unter "Ausgang" sucht,
    // will Gesendetes durchsuchen, nicht plötzlich alles sehen.
    const aktuellerReiter = REITER.find((r) => r.key === reiter);
    const [list, c, draftThreads, alleEntwuerfe] = await Promise.all([
      !aktuellerReiter ? Promise.resolve([] as MailRow[]) : crm.mailbox.list({
        ...aktuellerReiter.args,
        ...(suche.trim() ? { search: suche.trim() } : {}),
        // Wie die Suche grenzt das Postfach den Reiter weiter ein, statt
        // ihn zu ersetzen.
        ...(postfach ? { accountId: postfach } : {}),
      }),
      crm.mailbox.counts(),
      crm.drafts.threads(),
      crm.drafts.overview(),
    ]);
    setRows(list);
    setEntwuerfe(alleEntwuerfe);
    setCounts(c);
    setThreadsMitEntwurf(new Set(draftThreads));
    // Die Kopfzeile zeigt die zuvor geladene Fassung der Auswahl. Ohne
    // Abgleich bliebe sie beim alten Stand stehen, etwa "ungelesen" nach dem
    // Öffnen oder ohne den Projekttitel nach einer frischen Zuordnung.
    setSelected((prev) => (prev ? (list.find((r) => r.id === prev.id) ?? prev) : prev));
    setLoading(false);
  }, [reiter, suche, postfach]);

  // Kurz abwarten, damit nicht jeder Tastendruck eine Abfrage ausloest.
  const ersterLauf = useRef(true);
  useEffect(() => {
    if (ersterLauf.current) {
      ersterLauf.current = false;
      void load();
      return;
    }
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  const gegenseite = selected ? counterpartAddress(selected) : null;
  useEffect(() => {
    setAdresseBekannt(null);
    if (!gegenseite) return;
    let abgebrochen = false;
    void crm.contacts.forAddress(gegenseite).then((k) => {
      if (!abgebrochen) setAdresseBekannt(k !== null);
    });
    return () => {
      abgebrochen = true;
    };
    // `rows` mit: nach dem Anlegen laedt die Liste neu, und der Knopf muss gehen.
  }, [gegenseite, rows]);

  useEffect(() => {
    void crm.audiences.list().then(setAudiences);
    void crm.mailAccounts.list().then(setKonten);
  }, []);

  /**
   * Entwürfe zur aktuellen Auswahl laden.
   *
   * Die einzige Stelle, die entscheidet, wie Entwürfe zu einer Auswahl
   * gehören: über den Thread, wenn die Nachricht einen hat, sonst über den
   * Kontakt — `thread_id` ist nullbar (core/src/db/schema.ts). `open` und
   * jede Stelle, die nach einem Verwerfen oder Versand neu laden muss, rufen
   * ausschließlich diese Funktion auf. Zwei Stellen, die das unabhängig
   * voneinander entscheiden, laufen sonst auseinander — etwa ein Reload, der
   * nur bei vorhandenem Thread feuert, während das erste Laden auch ohne
   * einen funktioniert.
   */
  const ladeDrafts = async (row: MailRow) => {
    const draftsForRow = row.threadId
      ? await crm.drafts.list({ threadId: row.threadId })
      : await crm.drafts.list({ contactId: row.contactId ?? undefined });
    setDrafts(draftsForRow);

    // Entwürfe-Indikator in der Liste synchronisieren
    if (row.threadId) {
      const threadId = row.threadId;
      setThreadsMitEntwurf((prev) => {
        const updated = new Set(prev);
        if (draftsForRow.length === 0) {
          // Keine Entwürfe mehr: aus der Menge entfernen
          updated.delete(threadId);
        } else {
          // Entwürfe vorhanden: in die Menge aufnehmen
          updated.add(threadId);
        }
        return updated;
      });
    }
  };

  const open = async (row: MailRow) => {
    setSelected(row);
    setThread(row.threadId ? await crm.mailbox.thread(row.threadId) : null);
    await ladeDrafts(row);
    if (!row.seen) {
      await crm.mailbox.markSeen(row.id);
      await load();
    }
  };

  const sync = async () => {
    setSyncing(true);
    setHinweis(null);
    try {
      const res = await crm.mailAccounts.syncNow();
      setHinweis(
        res.errors.length
          ? res.errors.join(' · ')
          : res.stored > 0
            ? `${res.stored} neue Nachricht${res.stored === 1 ? '' : 'en'}`
            : 'Keine neuen Nachrichten',
      );
      await load();
    } catch (err) {
      setHinweis(err instanceof Error ? err.message : 'Abruf fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  // Die eingegangene Nachricht, um die es geht: die ausgewaehlte selbst, oder
  // — ist das eine eigene Mail — die juengste Antwort im Verlauf. Der Verlauf
  // kommt neueste zuerst (`listThread`).
  const bewertungsZiel: EmailMessage | null = selected
    ? ((thread ?? []).find((m) => m.id === selected.id && m.direction === 'inbound') ??
      (thread ?? []).find((m) => m.direction === 'inbound') ??
      null)
    : null;
  // Geantwortet wird auf die juengste Nachricht der Gegenseite; gibt es keine,
  // auf die ausgewaehlte.
  const antwortZiel: EmailMessage | null =
    bewertungsZiel ?? (thread ?? []).find((m) => m.id === selected?.id) ?? thread?.[0] ?? null;

  const nachBewertung = async () => {
    if (selected?.threadId) setThread(await crm.mailbox.thread(selected.threadId));
    await load();
  };

  // Die Bezugsnachricht eines geöffneten Entwurfs: nur gesetzt, wenn sie
  // tatsächlich im geladenen Thread steckt. Ohne Thread (die Nachricht selbst
  // hat keinen) oder bei einem Entwurf für eine andere Unterhaltung ist sie
  // nicht da — der Dialog öffnet sich dann nicht, statt mit einer nicht
  // vorhandenen Nachricht abzustürzen.
  const entwurfOeffnen = (d: EmailDraft) => {
    const bezug = (thread ?? []).find((m) => m.id === d.replyToMessageId);
    if (bezug) setOffenerEntwurf({ draft: d, bezug });
  };
  // Der Entwurf zur offenen Unterhaltung, fuer den Knopf oben rechts.
  const entwurfHier = drafts.find((d) =>
    (thread ?? []).some((m) => m.id === d.replyToMessageId),
  );

  const nachEntwurf = async () => {
    if (selected) {
      if (selected.threadId) setThread(await crm.mailbox.thread(selected.threadId));
      await ladeDrafts(selected);
    }
    await load();
  };

  return (
    <div className="space-y-4">
      <PageHeader>
        <div className="flex gap-1 border-b border-border">
          {REITER.map((r) => (
            <button
              key={r.key}
              onClick={() => {
                setReiter(r.key);
                setSelected(null);
                setThread(null);
              }}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                reiter === r.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {r.label}
              {counts && r.zahl(counts) > 0 && (
                <span className="ml-1.5 text-xs text-muted-foreground">{r.zahl(counts)}</span>
              )}
            </button>
          ))}
          <button
            onClick={() => {
              setReiter('drafts');
              setSelected(null);
              setThread(null);
            }}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              reiter === 'drafts'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            Entwürfe
            {entwuerfe.length > 0 && (
              <span className="ml-1.5 text-xs text-amber-600">{entwuerfe.length}</span>
            )}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          {/*
            Suche und Postfach stehen zusammen links: beide grenzen den
            gewaehlten Reiter weiter ein, waehrend rechts die Aktionen sitzen.
          */}
          <div className="flex min-w-[14rem] flex-1 items-center gap-2">
            <div className="relative min-w-[12rem] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="In E-Mails suchen …"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                aria-label="E-Mails durchsuchen"
              />
            </div>

            {mehrereKonten && (
              <select
                className="h-10 shrink-0 rounded-md border border-input bg-background px-3 text-sm"
                value={postfach}
                onChange={(e) => {
                  setPostfach(e.target.value);
                  // Wie beim Reiterwechsel: die offene Nachricht gehoert
                  // womoeglich nicht mehr zur Liste.
                  setSelected(null);
                  setThread(null);
                }}
                aria-label="Nach Postfach filtern"
              >
                <option value="">Alle Postfächer</option>
                {aktiveKonten.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex items-center gap-2">
            {counts && counts.unseen > 0 && (
              <Button
                variant="outline"
                onClick={async () => {
                  await crm.mailbox.markAllSeen();
                  await load();
                }}
              >
                <CheckCheck className="mr-2 h-4 w-4" />
                Alle gelesen
              </Button>
            )}
            <Button onClick={() => setComposing(true)}>
              <PenSquare className="mr-2 h-4 w-4" />
              Neue E-Mail
            </Button>
            <Button variant="outline" disabled={syncing} onClick={() => void sync()}>
              <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Rufe ab …' : 'Jetzt abrufen'}
            </Button>
          </div>
        </div>
      </PageHeader>

      {konten !== null && aktiveKonten.length === 0 && (
        <Card className="border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          Es ist noch kein Postfach eingerichtet. Bis dahin kommt hier nichts an
          und es geht nichts raus — unter{' '}
          <button className="underline" onClick={() => navigate('/einstellungen')}>
            Einstellungen
          </button>{' '}
          ein Postfach anlegen.
        </Card>
      )}

      {hinweis && (
        <Card className="p-3 text-sm text-muted-foreground">{hinweis}</Card>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_1fr]">
        <Card className="max-h-[70vh] overflow-y-auto">
          {loading ? (
            <p className="p-6 text-sm text-muted-foreground">Lade …</p>
          ) : reiter === 'drafts' ? (
            <EntwurfListe
              entwuerfe={entwuerfe}
              suche={suche}
              onOpen={(d) => {
                if (d.replyTo) setOffenerEntwurf({ draft: d, bezug: d.replyTo });
                else navigate(`/kontakte/${d.contactId}`);
              }}
            />
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              {suche.trim()
                ? `Keine E-Mail passt zu „${suche.trim()}“.`
                : LEERE_LISTE[reiter]}
            </p>
          ) : (
            <ul>
              {rows.map((m) => (
                <li key={m.id}>
                  <button
                    onClick={() => void open(m)}
                    className={`w-full border-b px-4 py-3 text-left last:border-0 hover:bg-muted/40 ${
                      selected?.id === m.id ? 'bg-muted/60' : ''
                    }`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
                      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <span
                          className={`truncate text-sm ${m.seen ? '' : 'font-semibold'}`}
                        >
                          {m.contactName ?? m.fromAddress ?? 'Unbekannt'}
                        </span>
                        {m.isCustomer ? (
                          <Badge variant="success">Kunde</Badge>
                        ) : m.contactId || m.organizationId ? (
                          <Badge variant="neutral">Lead</Badge>
                        ) : (
                          <Badge variant="warning">Unbekannt</Badge>
                        )}
                        {m.replySentiment && (
                          <Badge variant={m.replySentiment === 'positiv' ? 'success' : 'danger'}>
                            {SENTIMENT_LABEL[m.replySentiment]}
                          </Badge>
                        )}
                        {m.projectTitle && (
                          <Badge
                            variant="info"
                            title={m.projectTitle}
                            className="max-w-[9rem] truncate"
                          >
                            {m.projectTitle}
                          </Badge>
                        )}
                      </span>
                      <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                        {mehrereKonten && m.accountLabel && (
                          <span
                            className="max-w-[7rem] truncate rounded bg-muted px-1.5 py-0.5 text-[11px] font-normal"
                            title={`Postfach: ${m.accountLabel}`}
                          >
                            {m.accountLabel}
                          </span>
                        )}
                        {m.receivedAt
                          ? new Date(m.receivedAt).toLocaleDateString('de-DE')
                          : ''}
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-sm">
                      {m.direction === 'outbound' && (
                        <span className="mr-1 text-muted-foreground">↗</span>
                      )}
                      {m.subject || '(ohne Betreff)'}
                      {m.threadId && threadsMitEntwurf.has(m.threadId) && (
                        <span
                          title="Ein Entwurf liegt bereit"
                          aria-label="Ein Entwurf liegt bereit"
                          className="text-amber-600"
                        >
                          ✎
                        </span>
                      )}
                      {m.hasAttachments && (
                        <Paperclip className="ml-1 inline h-3 w-3 text-muted-foreground" />
                      )}
                    </p>
                    {m.snippet && (
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {m.snippet}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="max-h-[70vh] overflow-y-auto p-6">
          {!selected ? (
            <p className="text-sm text-muted-foreground">
              {reiter === 'drafts'
                ? 'Einen Entwurf anklicken, um ihn weiterzuschreiben.'
                : 'Eine Nachricht auswählen.'}
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-semibold">
                    {selected.subject || '(ohne Betreff)'}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {selected.fromAddress}
                    {selected.contactName && (
                      <>
                        {' · '}
                        <button
                          className="hover:underline"
                          onClick={() => navigate(`/kontakte/${selected.contactId}`)}
                        >
                          {selected.contactName}
                        </button>
                      </>
                    )}
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  {entwurfHier && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-500/60 text-amber-700 hover:bg-amber-50 dark:text-amber-500 dark:hover:bg-amber-950/30"
                      onClick={() => entwurfOeffnen(entwurfHier)}
                    >
                      <FilePen className="mr-2 h-4 w-4" />
                      Entwurf fortsetzen
                    </Button>
                  )}
                  {antwortZiel && (
                    <Button size="sm" onClick={() => setReplying(antwortZiel)}>
                      <Reply className="mr-2 h-4 w-4" />
                      Antworten
                    </Button>
                  )}
                  {mehrereKonten && selected.accountLabel && (
                    <span
                      className="max-w-[10rem] truncate rounded bg-muted px-1.5 py-0.5 text-[11px] font-normal text-muted-foreground"
                      title={`Postfach: ${selected.accountLabel}`}
                    >
                      {selected.accountLabel}
                    </span>
                  )}
                  {/*
                    Am Kontakt, nicht am Zuordnungsvermerk: eine Mail, die nur
                    ueber die Domain bei der Firma gelandet ist, gilt bereits
                    als „assigned“ — ein Mensch haengt trotzdem nicht dran.
                    Genau das ist der Fall, wenn `sekretariat@` auf eine
                    Erstansprache an `info@` antwortet, und genau dort wurde der
                    Knopf bisher ausgeblendet.
                  */}
                  {!selected.contactId && selected.assignment !== 'ignored' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAssigning({ row: selected, modus: 'vorhanden' })}
                    >
                      <Link2 className="mr-2 h-4 w-4" />
                      Kontakt zuordnen
                    </Button>
                  )}
                  {adresseBekannt === false && selected.assignment !== 'ignored' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAssigning({ row: selected, modus: 'neu' })}
                    >
                      <UserPlus className="mr-2 h-4 w-4" />
                      Kontakt anlegen
                    </Button>
                  )}
                  {selected.threadId && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAssigningProject(selected)}
                    >
                      <FolderKanban className="mr-2 h-4 w-4" />
                      Zu Projekt zuordnen
                    </Button>
                  )}
                  {selected.assignment !== 'ignored' && (
                    <Button variant="ghost" size="sm" onClick={() => setIgnoring(selected)}>
                      <EyeOff className="mr-2 h-4 w-4" />
                      Ausblenden
                    </Button>
                  )}
                </div>
              </div>

              {/*
                Dieselbe Bewertung wie auf der Antwortseite (`/leads/antworten`), an derselben
                Nachricht: was hier gedrueckt wird, steht dort genauso — und
                umgekehrt. Nur bei Post zu einer Erstansprache, wo es eine
                Kampagne oder ein Nachfassen gibt, das davon abhaengt.
              */}
              {bewertungsZiel && (selected.outreachMessageId || selected.campaignId) && (
                <div className="flex flex-wrap items-center gap-1.5 rounded-md border bg-muted/30 px-3 py-2">
                  <span className="mr-1 text-xs font-medium text-muted-foreground">
                    Antwort{selected.campaignName ? ` auf „${selected.campaignName}“` : ''}:
                  </span>
                  <ReplySentimentButtons
                    messageId={bewertungsZiel.id}
                    value={bewertungsZiel.replySentiment ?? null}
                    onChanged={() => void nachBewertung()}
                    onError={setHinweis}
                  />
                </div>
              )}

              {(thread ?? []).map((m) => (
                <div key={m.id} className="space-y-2">
                  <article
                    className={`rounded-md border p-4 ${
                      m.direction === 'outbound'
                        ? 'border-l-4 border-l-primary/40 bg-muted/30'
                        : ''
                    }`}
                  >
                    <header className="space-y-0.5 border-b pb-2 text-xs text-muted-foreground">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-medium text-foreground">
                          {m.direction === 'outbound' ? 'Von uns' : m.fromAddress}
                        </span>
                        <time dateTime={m.receivedAt ?? undefined}>
                          {m.receivedAt
                            ? new Date(m.receivedAt).toLocaleString('de-DE', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })
                            : ''}
                        </time>
                      </div>
                      <div>An: {m.toAddress ?? '—'}</div>
                      {m.ccAddresses && <div>Cc: {m.ccAddresses}</div>}
                    </header>

                    <ErstanspracheHinweis
                      message={m}
                      onChanged={async () => {
                        if (selected.threadId)
                          setThread(await crm.mailbox.thread(selected.threadId));
                      }}
                    />

                    <MessageBody text={m.body} />
                  </article>

                  {drafts
                    .filter((d) => d.replyToMessageId === m.id)
                    .map((d) => (
                      <DraftCard
                        key={d.id}
                        draft={d}
                        onOpen={entwurfOeffnen}
                        onDiscarded={() => void ladeDrafts(selected)}
                      />
                    ))}
                </div>
              ))}

              {/* Entwürfe, die zu keiner sichtbaren Nachricht gehören: ohne
                  Bezug, oder mit einem Bezug außerhalb des geladenen Threads
                  — etwa weil die Nachricht selbst keinen Thread hat und die
                  Entwürfe nur über den Kontakt geladen wurden (`thread` ist
                  dann `null`, also matcht `thread?.some(...)` nichts). Ohne
                  diesen Auffangkorb blieben solche Entwürfe unsichtbar im
                  Zustand stehen. */}
              {drafts
                .filter((d) => !thread?.some((m) => m.id === d.replyToMessageId))
                .map((d) => (
                  <DraftCard
                    key={d.id}
                    draft={d}
                    onOpen={entwurfOeffnen}
                    onDiscarded={() => void ladeDrafts(selected)}
                  />
                ))}
            </div>
          )}
        </Card>
      </div>

      {assigning && (
        <AssignMessageDialog
          message={assigning.row}
          initialModus={assigning.modus}
          audiences={audiences}
          onClose={() => setAssigning(null)}
          onDone={async () => {
            setAssigning(null);
            setSelected(null);
            await load();
          }}
        />
      )}

      {assigningProject && (
        <AssignProjectDialog
          message={assigningProject}
          onClose={() => setAssigningProject(null)}
          onDone={async () => {
            setAssigningProject(null);
            await load();
          }}
        />
      )}

      {replying && (
        <ReplyDialog
          message={replying}
          onClose={() => setReplying(null)}
          onSent={async () => {
            setReplying(null);
            if (selected?.threadId) setThread(await crm.mailbox.thread(selected.threadId));
            await load();
          }}
          onDraftSaved={() => void nachEntwurf()}
        />
      )}

      {offenerEntwurf && (
        <ReplyDialog
          message={offenerEntwurf.bezug}
          initialSubject={offenerEntwurf.draft.subject}
          initialBody={offenerEntwurf.draft.body}
          draftId={offenerEntwurf.draft.id}
          onClose={() => setOffenerEntwurf(null)}
          onSent={async () => {
            setOffenerEntwurf(null);
            await nachEntwurf();
          }}
          onDraftSaved={() => void nachEntwurf()}
        />
      )}

      {composing && (
        <ComposeDialog
          onClose={() => setComposing(false)}
          onSent={() => void load()}
        />
      )}

      {ignoring && (
        <ConfirmDialog
          title="Nachricht ausblenden?"
          confirmLabel="Ausblenden"
          onCancel={() => setIgnoring(null)}
          onConfirm={async () => {
            await crm.mailbox.ignore(ignoring.id);
            setIgnoring(null);
            setSelected(null);
            await load();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Die Nachricht wird nicht gelöscht, sondern wandert nach „Ignoriert“.
            Wenn dieser Absender nie erscheinen soll, lässt sich unter
            Einstellungen eine Regel dafür anlegen.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

/**
 * Der Reiter „Entwürfe“: angefangene Antworten, zuletzt bearbeitet zuerst.
 *
 * Ein Klick setzt den Entwurf im Antwortfenster fort, mit der Mail daneben.
 * Ein Entwurf ohne Bezugsmail (eine neue Mail, etwa von Claude vorbereitet)
 * hat kein Antwortfenster — der fuehrt in die Kontaktakte, wo er steht.
 */
function EntwurfListe({
  entwuerfe,
  suche,
  onOpen,
}: {
  entwuerfe: DraftOverviewRow[];
  suche: string;
  onOpen: (d: DraftOverviewRow) => void;
}) {
  const begriff = suche.trim().toLowerCase();
  const sichtbar = begriff
    ? entwuerfe.filter((d) =>
        [d.contactName, d.organizationName, d.subject, d.body].some((f) =>
          f?.toLowerCase().includes(begriff),
        ),
      )
    : entwuerfe;

  if (sichtbar.length === 0) {
    return (
      <p className="p-6 text-sm text-muted-foreground">
        {begriff ? `Kein Entwurf passt zu „${suche.trim()}“.` : LEERE_LISTE.drafts}
      </p>
    );
  }

  return (
    <ul>
      {sichtbar.map((d) => (
        <li key={d.id}>
          <button
            onClick={() => onOpen(d)}
            className="w-full border-b px-4 py-3 text-left last:border-0 hover:bg-muted/40"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-sm font-medium">{d.contactName}</span>
                <Badge variant="warning">Entwurf</Badge>
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {new Date(d.updatedAt).toLocaleDateString('de-DE')}
              </span>
            </div>
            <p className="mt-0.5 truncate text-sm">
              {d.subject || '(kein Betreff)'}
            </p>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {d.replyTo
                ? `Antwort auf „${d.replyTo.subject || 'ohne Betreff'}“`
                : 'Neue Mail — in der Kontaktakte öffnen'}
              {d.organizationName ? ` · ${d.organizationName}` : ''}
            </p>
          </button>
        </li>
      ))}
    </ul>
  );
}
